import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  IconButton,
  Button,
  Paper,
  InputAdornment,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SpaIcon from "@mui/icons-material/Spa";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DevicesIcon from "@mui/icons-material/Devices";
import CodeIcon from "@mui/icons-material/Code";
import BrushIcon from "@mui/icons-material/Brush";
import LanguageIcon from "@mui/icons-material/Language";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import { useSettings } from "../../../context/SettingsContext";

export default function Services() {
  const theme = useTheme();
  const { services, updateServices } = useSettings();

  const [draft, setDraft] = useState([]);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(services);
  }, [services]);

  const addService = () => {
    const v = value.trim();
    if (!v || draft.includes(v)) return;
    setDraft((p) => [...p, v]);
    setValue("");
  };

  const removeService = (service) => {
    setDraft((p) => p.filter((s) => s !== service));
  };

  const save = async () => {
    setSaving(true);
    await updateServices(draft);
    setSaving(false);
  };

  // Get icon based on service name
  const getServiceIcon = (name) => {
    const lower = name.toLowerCase();
    const iconSx = { fontSize: 16, color: theme.palette.primary.main };

    if (lower.includes("skin") || lower.includes("spa") || lower.includes("beauty"))
      return <SpaIcon sx={iconSx} />;
    if (lower.includes("hair") || lower.includes("salon") || lower.includes("cut"))
      return <ContentCutIcon sx={iconSx} />;
    if (lower.includes("electronic") || lower.includes("device") || lower.includes("tech"))
      return <DevicesIcon sx={iconSx} />;
    if (lower.includes("web") || lower.includes("development") || lower.includes("software"))
      return <CodeIcon sx={iconSx} />;
    if (lower.includes("design") || lower.includes("graphic") || lower.includes("art"))
      return <BrushIcon sx={iconSx} />;
    if (lower.includes("website") || lower.includes("seo") || lower.includes("digital"))
      return <LanguageIcon sx={iconSx} />;
    if (lower.includes("google") || lower.includes("ads") || lower.includes("marketing"))
      return <CampaignIcon sx={iconSx} />;
    if (lower.includes("offer") || lower.includes("deal") || lower.includes("sale"))
      return <LocalOfferIcon sx={iconSx} />;

    return <MiscellaneousServicesIcon sx={iconSx} />;
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          bgcolor: "#fff",
        }}
      >
        {/* Search input + Add button */}
        <Stack direction="row" gap={1.5} mb={3}>
          <TextField
            size="small"
            placeholder="Add new service..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addService()}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#f8fafc",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
              },
            }}
          />
          <IconButton
            onClick={addService}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: "#fff",
              width: 40,
              height: 40,
              borderRadius: "50%",
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>

        {/* Active Services label */}
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1.5,
          }}
        >
          Active Services
        </Typography>

        {/* Service chips */}
        <Stack direction="row" flexWrap="wrap" gap={1.2} mb={3}>
          {draft.map((service) => (
            <Chip
              key={service}
              label={service}
              onDelete={() => removeService(service)}
              deleteIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              icon={getServiceIcon(service)}
              sx={{
                px: 1,
                py: 0.5,
                fontWeight: 500,
                fontSize: "0.8125rem",
                bgcolor: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: "20px",
                "& .MuiChip-icon": {
                  color: `${theme.palette.primary.main} !important`,
                },
                "& .MuiChip-deleteIcon": {
                  color: "#94a3b8",
                  "&:hover": { color: "#64748b" },
                },
              }}
            />
          ))}
        </Stack>

        {/* Save button */}
        <Button
          onClick={save}
          disabled={saving}
          variant="contained"
          startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
          disableElevation
          sx={{
            textTransform: "none",
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": { bgcolor: theme.palette.primary.dark },
          }}
        >
          {saving ? "Saving..." : "SAVE CHANGES"}
        </Button>
      </Paper>

      {/* Info text below card */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2, px: 0.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8", mt: 0.25 }} />
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.5 }}>
          Add or remove services that your business offers. These will be available for tagging leads and creating specialized booking pipelines. Remember to click save to apply your changes system-wide.
        </Typography>
      </Box>
    </Box>
  );
}
